/**
 * FacilitatorDashboard.jsx — Section 10.2
 *
 * Event-level stats for facilitators:
 * - Number of guests in current session
 * - Beats created
 * - Exports requested
 * - Emails captured
 * - Subscription conversions
 */

import React, { useState, useEffect } from 'react';
import { Users, Music, Download, Mail, Star, ArrowLeft, Loader2 } from 'lucide-react';
import { useAuth } from '@/state/useAuth';
import { expoCsvFilename } from '@/utils/expoExport';
import { listClassPrograms } from '@/api/classPrograms';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const FacilitatorDashboard = ({ eventName, onBack }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [consentedOnly, setConsentedOnly] = useState(true);
  const [csvError, setCsvError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [programs, setPrograms] = useState([]);
  const { getToken } = useAuth();

  const downloadCsv = async () => {
    setDownloading(true); setCsvError(null);
    try {
      const params = new URLSearchParams({ consented_only: String(consentedOnly) });
      if (eventName) params.set('event', eventName);
      const res = await fetch(`${BACKEND_URL}/api/v1/platform/admin/export-captures.csv?${params}`, {
        headers: { Authorization: `Bearer ${getToken() || ''}` },
      });
      if (res.status === 401 || res.status === 403) throw new Error('Sign in as an admin to download');
      if (!res.ok) throw new Error('Could not download emails');
      const href = URL.createObjectURL(await res.blob());
      const link = document.createElement('a');
      link.href = href; link.download = expoCsvFilename(eventName); link.click();
      setTimeout(() => URL.revokeObjectURL(href), 1000);
    } catch (error) { setCsvError(error.message); } finally { setDownloading(false); }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const params = eventName ? `?event=${encodeURIComponent(eventName)}` : '';
        const res = await fetch(`${BACKEND_URL}/api/v1/guest/stats${params}`);
        if (res.ok) setStats(await res.json());
      } catch (e) {
        console.warn('Dashboard fetch error:', e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
    const interval = setInterval(load, 15000); // Refresh every 15s
    return () => clearInterval(interval);
  }, [eventName]);

  useEffect(() => {
    let active = true;
    listClassPrograms().then((rows) => { if (active) setPrograms(rows); }).catch(() => {});
    return () => { active = false; };
  }, []);

  const cards = [
    { label: 'Active Guests', value: stats?.total_guests ?? 0, icon: Users, color: 'var(--green)', source: 'guest_sessions WHERE expires_at > now' },
    { label: 'Beats Created', value: stats?.beats_created ?? 0, icon: Music, color: 'var(--purple)', source: 'guest_sessions WHERE arrangement_data != empty' },
    { label: 'Exports Requested', value: stats?.exports_requested ?? 0, icon: Download, color: 'var(--orange)', source: 'guest_sessions WHERE export_url IS NOT NULL' },
    { label: 'Emails Captured', value: stats?.emails_captured ?? 0, icon: Mail, color: 'var(--blue)', source: 'guest_sessions WHERE email IS NOT NULL' },
    { label: 'Conversions', value: stats?.conversions ?? 0, icon: Star, color: 'var(--green-bass)', source: 'guest_sessions WHERE converted = 1' },
  ];

  return (
    <div data-testid="facilitator-dashboard" style={{ minHeight: '100vh', background: 'var(--bg)', padding: 32 }}>
      {/* Header */}
      <div className="flex items-center gap-4" style={{ marginBottom: 32 }}>
        {onBack && (
          <button data-testid="dashboard-back" onClick={onBack} style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 22, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <ArrowLeft size={16} />
          </button>
        )}
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: 'var(--text)', margin: 0 }}>
            Event Dashboard
          </h1>
          {eventName && (
            <span style={{ fontSize: 13, color: 'var(--green)', fontWeight: 600 }}>{eventName}</span>
          )}
        </div>
        {loading && <Loader2 size={18} className="animate-spin" style={{ color: 'var(--text-dim)' }} />}
      </div>

      <div style={{ marginBottom: 24, padding: 16, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12 }}>
        <label className="flex items-center gap-2" style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 12 }}>
          <input type="checkbox" checked={consentedOnly} onChange={(e) => setConsentedOnly(e.target.checked)} />
          Consented only
        </label>
        <button data-testid="download-expo-csv" onClick={downloadCsv} disabled={downloading} style={{ background: 'var(--green)', color: 'var(--bg)', border: 'none', borderRadius: 20, padding: '9px 14px', fontWeight: 700, cursor: 'pointer' }}>
          {downloading ? 'Downloading…' : 'Download expo emails CSV'}
        </button>
        {csvError && <p data-testid="expo-csv-error" style={{ color: 'var(--pink)', fontSize: 12, marginTop: 8 }}>{csvError}</p>}
      </div>

      <section className="facilitator-programs" data-testid="facilitator-programs">
        <h2>Class programs</h2>
        <div>{programs.map((program) => <a key={program.id} href={`/facilitator/class/${program.id}`}><strong>{program.name}</strong><span>{program.class_code} · {program.claimed_count}/{program.seat_count} claimed</span></a>)}</div>
        {!programs.length && <p>No active or recently ended programs.</p>}
      </section>

      {/* Stats Cards */}
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.label}
              data-testid={`stat-${card.label.toLowerCase().replace(/\s/g, '-')}`}
              style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border)',
                borderRadius: 16,
                padding: '24px 20px',
              }}
            >
              <div className="flex items-center gap-2" style={{ marginBottom: 12 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: `${card.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon size={16} style={{ color: card.color }} />
                </div>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase' }}>
                  {card.label}
                </span>
              </div>
              <span style={{ fontSize: 36, fontWeight: 800, color: card.color }}>
                {card.value}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FacilitatorDashboard;
